import React from 'react';
import { Heart, Shield, UserPlus } from 'lucide-react';

interface Feature {
  name: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  href?: string;
}

const features: Feature[] = [
  {
    name: 'Expert Care',
    description: 'Our hospital backgrounds have equipped us to handle a broad range of medical concerns, from acute illnesses to chronic disease management.',
    icon: Heart,
    href: '#services'
  },
  {
    name: 'Patient-Centered Approach',
    description: 'We believe in listening, understanding, and collaborating with our patients to develop personalized treatment plans.',
    icon: UserPlus,
    href: '#contact'
  },
  {
    name: 'Continuity of Care',
    description: 'Our goal is to provide seamless healthcare, focusing on prevention, early intervention, and long-term wellness.',
    icon: Shield,
    href: '#services'
  }
];

export default function About() {
  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>, href?: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (href) {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <div 
      id="about" 
      className="py-24 bg-white"
      role="region"
      aria-labelledby="about-heading"
    >
      <div className="sr-only" aria-live="polite">
        Section 2 of 5 - About Us
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 
            id="about-heading"
            className="text-3xl font-bold text-gray-900 sm:text-4xl"
          >
            Welcome to Our Practice
          </h2>
          <p className="mt-6 text-lg text-gray-600 max-w-3xl mx-auto">
            At FMPCA, we are a team of dedicated and compassionate physicians with extensive experience as hospitalists. 
            After years of providing care to patients with complex and acute medical conditions, we are excited to bring our 
            expertise to the community through personalized, primary care services.
          </p>
        </div>

        <div className="mt-20">
          <h3 
            id="why-choose-us"
            className="text-2xl font-bold text-gray-900 text-center mb-12"
          >
            Why Choose Us?
          </h3>
          <div 
            className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
            role="list"
            aria-labelledby="why-choose-us"
          >
            {features.map((feature) => (
              <div 
                key={feature.name} 
                className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2"
                role="listitem"
                tabIndex={0}
                onKeyPress={(e) => handleKeyPress(e, feature.href)}
                onClick={() => feature.href && document.querySelector(feature.href)?.scrollIntoView({ behavior: 'smooth' })}
              >
                <div 
                  className="inline-flex items-center justify-center p-2 bg-primary-100 rounded-md"
                  aria-hidden="true"
                >
                  <feature.icon 
                    className="h-6 w-6 text-primary-700"
                    aria-hidden="true" 
                  />
                </div>
                <h4 
                  className="mt-4 text-lg font-medium text-gray-900"
                  id={`feature-${feature.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {feature.name}
                </h4>
                <p 
                  className="mt-2 text-gray-700"
                  aria-labelledby={`feature-${feature.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {feature.description}
                </p>
                {feature.href && (
                  <span className="sr-only">
                    Press Enter to learn more about {feature.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20">
          <div 
            className="bg-primary-50 rounded-lg p-8 hover:bg-primary-100 transition-colors cursor-pointer focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-4 group"
            role="region"
            aria-labelledby="mission-heading"
            tabIndex={0}
            onKeyPress={(e) => handleKeyPress(e, '#contact')}
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <h3 
              id="mission-heading"
              className="text-2xl font-bold text-gray-900 text-center mb-6 group-hover:text-primary-700 transition-colors"
            >
              Our Mission
            </h3>
            <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto group-hover:text-gray-800 transition-colors">
              We are committed to empowering our patients to live healthier lives by delivering comprehensive, 
              compassionate, and evidence-based care. Our practice is built on the principles of trust, 
              respect, and excellence.
            </p>
            <span className="sr-only">
              Press Enter to contact us and learn more about our mission
            </span>
            <div className="mt-6 flex justify-center opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
              <span className="inline-flex items-center text-primary-700 text-sm font-medium">
                Learn more about how we can help you
                <svg 
                  className="ml-2 h-4 w-4" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M14 5l7 7m0 0l-7 7m7-7H3" 
                  />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}